import React, { useState } from "react";
import {
  Search,
  ChevronDown,
  Mail,
  Phone,
  Book,
  ArrowRight,
  FileText,
  MapPin,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getLocalizedArticles } from "./CareGiverArticle";

const CareGiverHelp = () => {
  const { t } = useTranslation();
  const [activeFaq, setActiveFaq] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const articles = getLocalizedArticles(t);

  const faqs = [];
  for (let i = 1; i <= 12; i++) {
    faqs.push({
      q: t(`help.faqs.q${i}`),
      a: t(`help.faqs.a${i}`),
    });
  }

  const filteredArticles = articles.filter(
    (a) =>
      a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.category.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-5xl mx-auto pb-16 p-4">

      {/* Hero Section */}
      <div className="text-center py-12 px-4 mb-10">
        <h1 className="text-4xl md:text-5xl font-extrabold text-text dark:text-dark-text mb-4">
          {t("help.title")}
        </h1>
        <p className="text-lg text-gray-500 dark:text-dark-muted mb-10 max-w-2xl mx-auto">
          {t("help.subtitle")}
        </p>

        {/* Search */}
        <div className="max-w-2xl mx-auto relative z-10">
          <div className="flex items-center bg-card dark:bg-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border">
            <Search className="ml-6 text-gray-400 dark:text-dark-muted shrink-0" size={22} />
            <input
              type="text"
              placeholder={t("help.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-4 rounded-2xl bg-transparent border-none outline-none text-base text-text dark:text-dark-text placeholder-gray-400 dark:placeholder-dark-muted"
            />
          </div>

          {/* Search Results */}
          {searchTerm && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-2xl shadow-2xl border border-gray-100 dark:border-dark-border overflow-hidden max-h-80 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
              {filteredArticles.length > 0 ? (
                filteredArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/caregiver/help/article/${article.id}`}
                    className="block px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b border-gray-100 dark:border-dark-border/50 last:border-none text-left"
                  >
                    <p className="font-bold text-text dark:text-dark-text text-sm mb-1">
                      {article.title}
                    </p>
                    <span className="text-xs text-gray-400 dark:text-dark-muted">
                      {article.category}
                    </span>
                  </Link>
                ))
              ) : (
                <div className="p-6 text-gray-500 dark:text-dark-muted text-sm">
                  {t("help.noResults")} "{searchTerm}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Popular Articles */}
      <div className="mb-14">
        <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-6">
          {t("help.popularArticles")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {articles.slice(0, 6).map((article) => (
            <Link
              key={article.id}
              to={`/caregiver/help/article/${article.id}`}
              className="flex items-start gap-4 p-5 rounded-2xl bg-card dark:bg-dark-surface border border-gray-100 dark:border-dark-border hover:shadow-md hover:border-primary/30 dark:hover:border-primary/30 transition-all group"
            >
              <FileText size={20} className="text-primary shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
              <div className="min-w-0">
                <h3 className="font-bold text-text dark:text-dark-text mb-1 group-hover:text-primary transition-colors text-sm">
                  {article.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-dark-muted line-clamp-2">
                  {article.content}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* FAQ + Contact */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

        {/* FAQs */}
        <div className="lg:col-span-8">
          <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-6">
            {t("help.faq")}
          </h2>
          <div className="space-y-3">
            {faqs.map((item, i) => (
              <div
                key={i}
                className={`bg-card dark:bg-dark-surface rounded-2xl border transition-all duration-300 overflow-hidden ${
                  activeFaq === i
                    ? "border-primary/30 shadow-sm"
                    : "border-gray-100 dark:border-dark-border hover:border-primary/20"
                }`}
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex justify-between items-center p-5 text-left"
                >
                  <span
                    className={`font-semibold text-sm md:text-base pr-4 ${
                      activeFaq === i
                        ? "text-primary"
                        : "text-text dark:text-dark-text"
                    }`}
                  >
                    {item.q}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`text-gray-400 dark:text-dark-muted transition-transform duration-300 shrink-0 ${
                      activeFaq === i ? "rotate-180 text-primary" : ""
                    }`}
                  />
                </button>
                <div
                  className={`px-5 text-gray-500 dark:text-dark-muted text-sm leading-relaxed transition-all duration-300 ease-in-out ${
                    activeFaq === i
                      ? "max-h-64 pb-5 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  {item.a}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact / Office Info */}
        <div className="lg:col-span-4 space-y-4">
          <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-6">
            {t("help.contactUs")}
          </h2>

          {/* Office Address */}
          <div className="bg-card dark:bg-dark-surface rounded-2xl border border-gray-100 dark:border-dark-border p-6">
            <div className="flex items-center gap-3 mb-3">
              <MapPin size={20} className="text-primary shrink-0" />
              <h3 className="font-bold text-text dark:text-dark-text">{t("help.office")}</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-dark-muted leading-relaxed whitespace-pre-line">
              {t("help.officeAddress")}
            </p>
          </div>

          {/* Email */}
          <div className="bg-card dark:bg-dark-surface rounded-2xl border border-gray-100 dark:border-dark-border p-6">
            <div className="flex items-center gap-3 mb-3">
              <Mail size={20} className="text-primary shrink-0" />
              <h3 className="font-bold text-text dark:text-dark-text">{t("help.emailSupport")}</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-dark-muted mb-3">
              {t("help.emailDesc")}
            </p>
            <a
              href="mailto:info@mogzit.com"
              className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:underline"
            >
              info@mogzit.com <ArrowRight size={14} />
            </a>
          </div>

          {/* Phone */}
          <div className="bg-card dark:bg-dark-surface rounded-2xl border border-gray-100 dark:border-dark-border p-6">
            <div className="flex items-center gap-3 mb-3">
              <Phone size={20} className="text-primary shrink-0" />
              <h3 className="font-bold text-text dark:text-dark-text">{t("help.phone")}</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-dark-muted mb-3">
              {t("help.phoneDesc")}
            </p>
            <a
              href="tel:+251902993278"
              className="inline-flex items-center gap-2 text-sm font-bold text-primary hover:underline"
            >
              +251 902 993 278<br />+251 952 133 990 <ArrowRight size={14} />
            </a>
          </div>

          {/* Caregiver Guide */}
          <div className="bg-card dark:bg-dark-surface rounded-2xl border border-gray-100 dark:border-dark-border p-6">
            <div className="flex items-center gap-3 mb-3">
              <Book size={20} className="text-primary shrink-0" />
              <h3 className="font-bold text-text dark:text-dark-text">{t("help.guideTitle")}</h3>
            </div>
            <p className="text-sm text-gray-500 dark:text-dark-muted mb-4 leading-relaxed">
              {t("help.guideDesc")}
            </p>
            <Link
              to="/caregiver/help/article/1"
              className="w-full block text-center py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold text-sm transition-colors"
            >
              {t("help.readGuide")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareGiverHelp;
